import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Flex } from '../grid';
import { Icon } from '../icons';
import { P } from '../typography/new';

const CircleIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  border: 1px solid ${({ disabled, theme }) => (disabled ? theme.colors.grey30 : theme.colors.black)};
  padding: 8px;
  background: transparent;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  flex-shrink: 0;
`;

const AmountInput = styled.input`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  text-align: left;
  width: 61px;
  background: transparent;
  border: 0;
  outline: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.black};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey30};
    opacity: 1;
  }

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const ResetLink = styled.button`
  background: none;
  border: 0;
  padding: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: ${({ disabled, theme }) => (disabled ? theme.colors.grey30 : theme.colors.black)};
  text-decoration: underline;
`;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const roundToCents = value => Math.round(value * 100) / 100;
// Redemption stays cent-level, snapping a typed amount UP to the nearest 10c (e.g. 12.25 -> 12.30).
// 1 point = $1, so this is a display granularity choice, not a points-unit constraint. Worked in
// integer cents to avoid float drift.
const roundUpToPoint = value => (Math.ceil(Math.round(value * 100) / 10) * 10) / 100;

// Move to the adjacent multiple of `step`, snapping off-grid values back onto the grid and capping
// at [0, max]. Worked in integer cents to avoid float drift.
//   direction +1 -> next step up: 5.50 -> 6.00, but a 5.65 max caps it to 5.65
//   direction -1 -> next step down: an off-grid 5.65 (the capped max) -> 5.50
const stepToGrid = (value, step, max, direction) => {
  const stepCents = Math.round(step * 100);
  const units = Math.round(value * 100) / stepCents;
  const nextUnits = direction > 0 ? Math.floor(units) + 1 : Math.ceil(units) - 1;
  return clamp(roundToCents((nextUnits * stepCents) / 100), 0, max);
};

const PointsQuantityPicker = ({ amount, maxAmount, onChange, step }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(amount.toFixed(2));

  useEffect(() => {
    if (!isEditing) {
      setDraftValue(amount.toFixed(2));
    }
  }, [amount, isEditing]);

  const handleDecrement = () => onChange(stepToGrid(amount, step, maxAmount, -1));
  const handleIncrement = () => onChange(stepToGrid(amount, step, maxAmount, +1));
  // Reset restores the maximum chip-in (the highest amount redeemable) rather than clearing to 0.
  const handleReset = () => onChange(maxAmount);

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const handleInputChange = event => {
    setDraftValue(event.target.value);
  };

  const handleInputBlur = () => {
    const parsed = parseFloat(draftValue);
    const next = Number.isFinite(parsed) ? clamp(roundUpToPoint(parsed), 0, maxAmount) : amount;
    setDraftValue(next.toFixed(2));
    setIsEditing(false);
    if (next !== amount) {
      onChange(next);
    }
  };

  const handleInputKeyDown = event => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  return (
    <Flex flexDirection="column" alignItems="center" gap="12px">
      <Flex
        alignItems="center"
        justifyContent="center"
        gap="16px"
        backgroundColor="white"
        p="8px"
        style={{ borderRadius: '999px' }}
      >
        <CircleIconButton onClick={handleDecrement} disabled={amount <= 0} aria-label="Decrease amount" type="button">
          <Icon name="minus" height="20px" width="20px" fill={amount <= 0 ? '#B9B8B4' : '#000000'} />
        </CircleIconButton>

        <Flex alignItems="center">
          <P variant="medium-semi-bold" style={{ fontSize: '24px' }}>
            $
          </P>
          <AmountInput
            type="number"
            inputMode="decimal"
            // `step="any"` so the browser's native constraint validation accepts any cent value on form
            // submit (e.g. checkout "Confirm"). The numeric `step` prop only drives the +/- nudge buttons;
            // tying it to the DOM step attribute would reject typed amounts like 1.95 ("nearest valid: 1.5, 2").
            step="any"
            min={0}
            max={maxAmount}
            value={isEditing ? draftValue : amount.toFixed(2)}
            placeholder={maxAmount.toFixed(2)}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            aria-label="Points amount in dollars"
          />
        </Flex>

        <CircleIconButton
          onClick={handleIncrement}
          disabled={amount >= maxAmount}
          aria-label="Increase amount"
          type="button"
        >
          <Icon name="plus" height="20px" width="20px" fill={amount >= maxAmount ? '#B9B8B4' : '#000000'} />
        </CircleIconButton>
      </Flex>
      <ResetLink type="button" onClick={handleReset} disabled={amount >= maxAmount}>
        Reset
      </ResetLink>
    </Flex>
  );
};

PointsQuantityPicker.propTypes = {
  amount: PropTypes.number.isRequired,
  maxAmount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  step: PropTypes.number,
};

PointsQuantityPicker.defaultProps = {
  step: 0.5,
};

export { PointsQuantityPicker };
